import { cva } from 'class-variance-authority'
import { arc, megaeth, robinhood, tempo } from 'viem/chains'

import {
  BaseIcon,
  TempoIcon,
  ArbitrumIcon,
  ArcIcon,
  CeloIcon,
  EthereumIcon,
  OptimismIcon,
  WorldChainIcon,
  PolygonIcon,
  BscIcon,
  HyperEvmIcon,
  LineaIcon,
  megaethIcon,
  MonadIcon,
  RobinhoodIcon,
  ScrollIcon,
  ZoraIcon,
} from '~/components/chain-icons'
import { SupportedChainId } from '~/constants/chains'

export const iconVariants = cva(
  'rounded bg-surface ring-1 ring-white group-hover:ring-gray-50 dark:ring-[#090909] group-hover:dark:ring-gray-800 flex items-center justify-center',
  {
    variants: {
      size: {
        xs: 'h-3 w-3',
        sm: 'h-3.5 w-3.5',
        md: 'h-[1.125rem] w-[1.125rem]',
        ml: 'h-5 w-5',
        lg: 'h-6 w-6',
        xl: 'h-12 w-12 rounded-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

export const sizeMap = {
  xs: 8,
  sm: 10,
  md: 12,
  ml: 14,
  lg: 16,
  xl: 32,
}

export const chainIconMap: Record<
  number,
  React.ComponentType<{ className: string; size: number }>
> = {
  1: EthereumIcon,
  10: OptimismIcon,
  137: PolygonIcon,
  42161: ArbitrumIcon,
  8453: BaseIcon,
  42220: CeloIcon,
  480: WorldChainIcon,
  56: BscIcon,
  999: HyperEvmIcon,
  59144: LineaIcon,
  143: MonadIcon,
  534352: ScrollIcon,
  7777777: ZoraIcon,
  [tempo.id]: TempoIcon,
  [robinhood.id]: RobinhoodIcon,
  [megaeth.id]: megaethIcon,
  [arc.id]: ArcIcon,
}

export const getChainBackgroundColor = (chainId: SupportedChainId) => {
  switch (chainId) {
    case 1: // mainnet
      return 'bg-[#627EEA]/20 dark:bg-[#627EEA]/30'
    case 10: // optimism
      return 'bg-[#FF0420]/20 dark:bg-[#FF0420]/30'
    case 42161: // arbitrum
      return 'bg-[#28A0F0]/20 dark:bg-[#28A0F0]/30'
    case 8453: // base
      return 'bg-[#0052FF]/20 dark:bg-[#0052FF]/30'
    case 42220: // celo
      return 'bg-[#35D07F]/20 dark:bg-[#35D07F]/30'
    case 480: // worldchain
      return 'bg-[#bebfbb]/20 dark:bg-[#bebfbb]/30'
    case tempo.id: // tempo
      return 'bg-surface-strong'
    case 137: // polygon
      return 'bg-[#7B3FE4]/20 dark:bg-[#7B3FE4]/30'
    case 56: // bsc
      return 'bg-[#F0B90B]/20 dark:bg-[#F0B90B]/30'
    case 999: // hyperevm
      return 'bg-[#50D2C1]/20 dark:bg-[#50D2C1]/30'
    case 59144: // linea
      return 'bg-[#61DFFF]/20 dark:bg-[#61DFFF]/30'
    case 143: // monad
      return 'bg-[#836EF9]/20 dark:bg-[#836EF9]/30'
    case 534352: // scroll
      return 'bg-[#EBC28E]/20 dark:bg-[#EBC28E]/30'
    case robinhood.id:
      return 'bg-[#00C805]/20 dark:bg-[#00C805]/30'
    case megaeth.id:
      return 'bg-surface-strong'
    case arc.id:
      return 'bg-[#1B3158]/20 dark:bg-[#1B3158]/50'
    default:
      return 'bg-gray-200 dark:bg-gray-700'
  }
}

export const getChainTextColor = (chainId: SupportedChainId) => {
  switch (chainId) {
    case 1: // mainnet
      return 'text-[#627EEA]'
    case 10: // optimism
      return 'text-[#FF0420]'
    case 42161: // arbitrum
      return 'text-[#28A0F0]'
    case 8453: // base
      return 'text-[#0052FF]'
    case 42220: // celo
      return 'text-[#35D07F]'
    case 480: // worldchain
      return 'text-[#33312E] dark:text-white'
    case tempo.id: // tempo
      return 'text-[#33312E] dark:text-white'
    case 137: // polygon
      return 'text-[#7B3FE4]'
    case 56: // bsc
      return 'text-[#F0B90B]'
    case 999: // hyperevm
      return 'text-[#50D2C1]'
    case 59144: // linea
      return 'text-[#121212] dark:text-white'
    case 143: // monad
      return 'text-[#836EF9]'
    case 534352: // scroll
      return 'text-[#33312E] dark:text-white'
    case robinhood.id:
      return 'text-[#00C805]'
    case megaeth.id:
      return 'text-[#1A1A1B] dark:text-white'
    case arc.id:
      return 'text-[#1B3158] dark:text-white'
    default:
      return 'text-gray-700 dark:text-gray-300'
  }
}
